import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, PasswordInput } from '@mlm/ui';
import { changePassword } from '../../lib/api';

export function PasswordTab() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mut = useMutation({
    meta: { successMessage: 'Password changed.' },
    mutationFn: () => changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrent('');
      setNew('');
      setConfirm('');
      setError(null);
      setDone(true);
      setTimeout(() => setDone(false), 2400);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status === 401) setError('Current password is incorrect.');
      else if (e.response?.status === 429)
        setError('Too many attempts. Please wait an hour before trying again.');
      else setError(e.response?.data?.error ?? 'Failed.');
    },
  });

  const mismatch = newPassword.length > 0 && confirm.length > 0 && newPassword !== confirm;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Change password</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground">Current password</label>
          <PasswordInput value={currentPassword} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">New password</label>
          <PasswordInput value={newPassword} onChange={(e) => setNew(e.target.value)} />
          <p className="text-[11px] text-muted-foreground mt-1">Minimum 8 characters.</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Confirm new password</label>
          <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {mismatch && <p className="text-xs text-destructive mt-1">Passwords don't match.</p>}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !currentPassword || newPassword.length < 8 || mismatch}
          size="sm"
        >
          {mut.isPending ? 'Updating…' : done ? 'Updated' : 'Update password'}
        </Button>
      </CardContent>
    </Card>
  );
}
